'use client'
import { AddCustomerAddress, UpdateCustomerAddress } from '@/actions'
import {
  ButtonRoundStyle,
  Form,
  PopupWrapper,
  RHFAutocomplete,
  RHFCheckbox,
  RHFRadioGroup,
  RHFTextField,
  RHFTextarea,
} from '@/components'
import { FieldLabel } from '@/components/form/rhf-text-field/components'
import { useLocationFields } from '@/features/orders/hooks'
import { useAccountStore } from '@/store/accounts'
import { deliveryAddressSchema } from '@/validations'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

interface AddressProps {
  defaultValue?: IAddressCustomer
  isModal: boolean
  onCancel: () => void
  type: 'add' | 'update'
}
export const ModalAddressForm = ({ onCancel, defaultValue, isModal, type }: AddressProps) => {
  const t = useTranslations('UserProfile')
  const { updateUser } = useAccountStore()
  const methods = useForm({
    resolver: yupResolver(deliveryAddressSchema),
  })

  const { watch, handleSubmit, setValue, control, reset } = methods
  // const { data: listAddressTypes } = useAddressTypes()
  const currentAddressType = '2024' //đang mặc định dùng địa chỉ cũ

  useEffect(() => {
    if (type == 'update' && !!defaultValue) {
      reset({
        firstname: defaultValue?.firstname ?? '',
        lastname: defaultValue?.lastname ?? '',
        middlename: defaultValue?.middlename ?? '',
        suffix: defaultValue?.suffix ?? '',
        telephone: defaultValue?.telephone ?? '',
        city: defaultValue?.city ?? '',
        region_id: defaultValue?.region_id ?? '',
        country_code: defaultValue?.country_code ?? 'VN',
        default_billing: defaultValue?.default_billing ?? '',
        default_shipping: defaultValue?.default_shipping ?? '',
        city_id:
          defaultValue?.custom_attributes?.find((attr) => attr.attribute_code === 'province_code')?.value ??
          '',

        district_id:
          defaultValue?.custom_attributes?.find((attr) => attr.attribute_code === 'district_code')?.value ??
          '',
        ward_id:
          defaultValue?.custom_attributes?.find((attr) => attr.attribute_code === 'ward_code')?.value ?? '',
        // street thuộc type là string, tuy nhiên input đang lưu text nên join cho chắc chắn
        street: Array.isArray(defaultValue?.street)
          ? defaultValue.street.join(', ')
          : (defaultValue?.street ?? ''),
        address: Array.isArray(defaultValue?.street)
          ? defaultValue.street.join(', ')
          : (defaultValue?.street ?? ''),
        vat_id: defaultValue?.vat_id ?? '',
        id: defaultValue?.id ?? '',
        custom_attributes: defaultValue?.custom_attributes ?? [],
        setDefault: !!defaultValue?.default_billing || !!defaultValue?.default_shipping ? true : false,
        tag: defaultValue?.tag ?? '',
        customer_notes: defaultValue?.customer_notes ?? '',
      } as any)

      const customerNotes = defaultValue?.custom_attributes?.find(
        (attr) => attr.attribute_code === 'customer_notes'
      )?.value
      if (customerNotes) {
        setValue('customer_notes' as any, customerNotes)
      }
    }
  }, [defaultValue, reset, type, setValue])

  const {
    dataCities: listCities,
    dataDistricts: listDistricts,
    dataWards: listWards,
    isLoadingCitys,
    isLoadingDistricts,
    isLoadingWards,
  } = useLocationFields({ control })
  // console.log('🚀 ~ listDistricts:', listDistricts)

  const mutationCreate = useMutation({
    mutationKey: ['AddCustomerAddress'],
    mutationFn: (data: any) => AddCustomerAddress(data),
    onSuccess: (data: any) => {},
    onError: () => {},
  })

  const mutationUpdate = useMutation({
    mutationKey: ['UpdateCustomerAddress'],
    mutationFn: (data: any) => UpdateCustomerAddress(data),
    onSuccess: (data: any) => {},
    onError: () => {},
  })

  const onSubmit = async (values: any) => {
    const formatData = {
      country_code: 'VN',
      city: listCities?.find((item: any) => item.id == values?.city_id)?.name ?? '',
      city_id: values?.city_id,
      district: listDistricts?.find((district: any) => district?.id == values?.district_id)?.name ?? '',
      ward: listWards?.find((ward: any) => ward?.id == +values?.ward_id)?.name ?? '',
      street: [values?.address ?? ''],

      ward_code: values?.ward_id || '',
      district_code: values?.district_id || '',
      province_code: values?.city_id || '',
      ward_name: listWards?.find((item: any) => item.id == values?.ward_id)?.name || '',
      district_name: listDistricts?.find((item: any) => item.id == values?.district_id)?.name || '',
      province_name: listCities?.find((item: any) => item.id == values?.city_id)?.name || '',
      country_id: 'VN',
      // postcode: '70000',
      address_type: currentAddressType,

      firstname: values?.firstname ?? '.', // bùa của BE không có là lỗi
      lastname: values?.lastname ?? ' ',
      telephone: values?.telephone, //missing mobile_phone on data -> fixed
      company: 'Mona Media',
      default_billing: !!values?.setDefault,
      default_shipping: !!values?.setDefault,
      tag: values?.tag || '',
      customer_notes: values?.customer_notes || '',
    }

    // console.log('🚀 ~ onSubmit ~ formatData:', formatData)

    const res = await mutationCreate.mutateAsync(formatData)

    if (res?.status === 'success') {
      toast.success('Tạo địa chỉ giao hàng thành công')
      await updateUser()
      reset()
      onCancel()
    } else if (res?.status === 'error') {
      toast.error(res?.message)
    }
  }

  const onUpdate = async (values: any) => {
    const formatData = {
      country_code: 'VN',
      city: listCities?.find((item: any) => item.id == values?.city_id)?.name ?? '',
      city_id: values?.city_id,
      district: listDistricts?.find((district: any) => district?.id == values?.district_id)?.name ?? '',
      ward: listWards?.find((ward: any) => ward?.id == +values?.ward_id)?.name ?? '',
      street: [values?.address ?? ''],

      ward_code: values?.ward_id || '',
      district_code: values?.district_id || '',
      province_code: values?.city_id || '',
      ward_name: listWards?.find((item: any) => item.id == values?.ward_id)?.name || '',
      district_name: listDistricts?.find((item: any) => item.id == values?.district_id)?.name || '',
      province_name: listCities?.find((item: any) => item.id == values?.city_id)?.name || '',
      country_id: 'VN',
      // postcode: '70000',
      address_type: currentAddressType,

      firstname: values?.firstname ?? '.', // bùa của BE không có là lỗi
      lastname: values?.lastname ?? ' ',
      telephone: values?.telephone, //missing mobile_phone on data -> fixed
      company: 'Mona Media',
      default_billing: !!values?.setDefault,
      default_shipping: !!values?.setDefault,
      tag: values?.tag || '',
      customer_notes: values?.customer_notes || '',
    }
    const dataSubmit = {
      values: formatData,
      id: defaultValue?.id,
    }
    const res = await mutationUpdate.mutateAsync(dataSubmit)
    if (res?.status === 'success') {
      toast.success('Cập nhật địa chỉ giao hàng thành công')
      await updateUser()
      reset()
      onCancel()
    } else if (res?.status === 'error') {
      toast.error(res?.message)
    }
  }

  return (
    <PopupWrapper isOpen={isModal} width="520px" maxHeight="85vh" handleClose={onCancel}>
      <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        {/* Fixed header */}
        <Typography sx={{ fontWeight: 500, mb: 2, textTransform: 'uppercase', flexShrink: 0 }} variant="h6">
          {defaultValue ? t('Cập nhật địa chỉ') : t('Thêm địa chỉ')}
        </Typography>

        <Form methods={methods} onSubmit={handleSubmit(type == 'add' ? onSubmit : onUpdate)}>
          {/* Scrollable content */}
          <Box sx={{ flex: 1, overflowY: 'auto', pr: 1, mb: 2, maxHeight: '50vh', mr: -1 }}>
            <Grid spacing={2} container columns={12}>
              <Grid size={{ xs: 12 }}>
                <FieldLabel label={'Đại chỉ này là:'} />
                <RHFRadioGroup
                  control={control as any}
                  name="tag"
                  options={[
                    {
                      label: (
                        <Box
                          sx={{
                            p: '.4em 1em',
                            borderRadius: '4px',
                            backgroundColor: '#252422',
                            color: '#fff',
                            fontSize: { xs: '12px', sm: '14px' },
                          }}
                        >
                          Nhà riêng
                        </Box>
                      ),
                      value: 'Nhà riêng',
                    },
                    {
                      label: (
                        <Box
                          sx={{
                            p: '.4em 1em',
                            borderRadius: '4px',
                            backgroundColor: '#F46A00',
                            color: '#fff',
                            fontSize: { xs: '12px', sm: '14px' },
                          }}
                        >
                          Người nhận hộ
                        </Box>
                      ),
                      value: 'Người nhận hộ',
                    },
                    {
                      label: (
                        <Box
                          sx={{
                            p: '.4em 1em',
                            borderRadius: '4px',
                            backgroundColor: '#0066D4',
                            color: '#fff',
                            fontSize: { xs: '12px', sm: '14px' },
                          }}
                        >
                          Công ty
                        </Box>
                      ),
                      value: 'Công ty',
                    },
                    {
                      label: (
                        <Box
                          sx={{
                            p: '.4em 1em',
                            borderRadius: '4px',
                            backgroundColor: '#B100D4',
                            color: '#fff',
                            fontSize: { xs: '12px', sm: '14px' },
                          }}
                        >
                          Khác
                        </Box>
                      ),
                      value: 'Khác',
                    },
                  ]}
                  // defaultValue={listPayment?.[0]?.code}
                  // customRender={paymentMethodRender}
                  className="w-full"
                  // value={paymentMethod}
                  // onChange={(e) => {
                  //   setPaymentMethod(e.target.value)
                  // }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                }}
              >
                <RHFTextField
                  name="lastname"
                  className="w-full"
                  placeholder="Nhập họ và tên"
                  label="Họ và tên"
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                }}
              >
                <RHFTextField
                  name="telephone"
                  className="w-full"
                  placeholder="Số điện thoại"
                  label="Số điện thoại"
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                }}
              >
                <RHFAutocomplete
                  className="w-full"
                  required
                  placeholder="Chọn thành phố"
                  loading={isLoadingCitys}
                  options={
                    listCities?.map((item) => ({
                      label: item?.name,
                      value: String(item?.id),
                    })) || []
                  }
                  label="Thành phố"
                  name="city_id"
                  onChange={() => {
                    setValue('district_id', '')
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <RHFAutocomplete
                  className="w-full"
                  loading={isLoadingDistricts}
                  required
                  placeholder="Chọn quận/huyện"
                  options={
                    listDistricts?.map((item) => ({
                      label: item?.name,
                      value: String(item?.id),
                    })) || []
                  }
                  label="Quận"
                  name="district_id"
                  onChange={() => {
                    setValue('ward_id', '')
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <RHFAutocomplete
                  className="w-full"
                  loading={isLoadingWards}
                  required
                  placeholder="Chọn phường"
                  options={
                    listWards?.map((item) => ({
                      label: item?.name,
                      value: String(item?.id),
                    })) || []
                  }
                  label="Phường"
                  name="ward_id"
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                }}
              >
                <RHFTextField
                  className="w-full"
                  required
                  placeholder="Nhập địa chỉ nhà"
                  label="Địa chỉ nhà"
                  name="address"
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                }}
              >
                <RHFTextarea placeholder="Ghi chú" label="Ghi chú" name="customer_notes" rows={3} />
              </Grid>
            </Grid>
          </Box>

          {/* Fixed footer */}
          <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2, flexShrink: 0 }}>
            <Stack
              spacing={{
                xs: 1,
                md: 2,
              }}
            >
              <RHFCheckbox name="setDefault" label={t('Đặt làm địa chỉ mặc định')} />

              <Typography
                color="#F04438"
                fontWeight={300}
                fontSize="0.875rem"
                sx={{
                  marginY: -1,
                }}
              >
                *{t('Lưu ý: sử dụng địa chỉ cũ (trước sáp nhập)')}
              </Typography>

              <ButtonRoundStyle
                variant="contained"
                type="submit"
                loading={mutationUpdate.isPending || mutationCreate.isPending}
                sx={{
                  minWidth: 200,
                  marginLeft: 'auto',
                }}
                disabled={type == 'update' ? mutationUpdate.isPending : mutationCreate.isPending}
              >
                {t('Lưu địa chỉ')}
              </ButtonRoundStyle>
            </Stack>
          </Box>
        </Form>
      </Box>
    </PopupWrapper>
  )
}
